/**
 * CompaniesPage Component
 * 
 * Main page for company management
 * Handles CRUD operations and integration management
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CompanyList } from '@/components/companies/CompanyList';
import { CompanyFormDialog } from '@/components/companies/CompanyFormDialog';
import { IntegrationConfigDialog } from '@/components/companies/IntegrationConfigDialog';
import { toast } from 'sonner';
import { mockCompanyService } from '@/data/mockCompanies';
import { getMockProductInstancesForCompany } from '@/data/mockProductInstances';
import type { CompanyListItem, CreateCompanyRequest, UpdateCompanyRequest } from '@/types';
import type { Company } from '@/features/multi-company/types/company.types';

export function CompaniesPage() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<CompanyListItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isIntegrationDialogOpen, setIsIntegrationDialogOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState<any | null>(null);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);

  // Load companies on mount
  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    setIsLoading(true);
    try {
      const data = await mockCompanyService.getCompanies();
      setCompanies(data);
    } catch (error) {
      console.error('Error loading companies:', error);
      toast.error('Failed to load companies');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCompanyClick = (companyId: string) => {
    // Open integration configuration dialog instead of navigating
    handleConfigureIntegrations(companyId);
  };

  const handleConfigureIntegrations = (companyId: string) => {
    const companyListItem = companies.find((c) => c.id === companyId);
    if (companyListItem) {
      // Load full company data with product instances
      const mockInstances = getMockProductInstancesForCompany(companyId);
      
      // Map mock instances to Company's ProductInstance type (add missing apiKey)
      const productInstances = mockInstances.map((instance) => ({
        ...instance,
        apiKey: null as string | null, // Add missing apiKey property
      }));
      
      const fullCompany: Company = {
        id: companyListItem.id,
        name: companyListItem.name,
        code: companyListItem.code,
        status: companyListItem.status,
        description: '',
        activeProjects: companyListItem.activeProjects,
        employeeCount: companyListItem.employeeCount,
        productInstances: productInstances,
        createdAt: new Date(),
        updatedAt: new Date(),
      };
      
      setSelectedCompany(fullCompany);
      setIsIntegrationDialogOpen(true);
    }
  };

  const handleCompanyEdit = async (companyId: string) => {
    try {
      // In a real app, fetch full company details
      // For now, we'll use the list item data
      const company = companies.find((c) => c.id === companyId);
      if (company) {
        // Convert CompanyListItem to Company for editing
        // This is a simplified version - in production, fetch full details
        const mockInstances = getMockProductInstancesForCompany(companyId);
        const productInstances = mockInstances.map((instance) => ({
          ...instance,
          apiKey: null as string | null,
        }));
        
        setEditingCompany({
          ...company,
          productInstances,
          financialIntegration: null,
          hrIntegration: null,
          createdAt: new Date(),
          updatedAt: new Date(),
        } as Company);
        setIsFormOpen(true);
      }
    } catch (error) {
      console.error('Error loading company for edit:', error);
      toast.error('Failed to load company details');
    }
  };

  const handleCompanyCreate = () => {
    setEditingCompany(null);
    setIsFormOpen(true);
  };

  const handleFormSubmit = async (data: CreateCompanyRequest | UpdateCompanyRequest) => {
    try {
      if (editingCompany) {
        // Update existing company
        await mockCompanyService.updateCompany(editingCompany.id, data as UpdateCompanyRequest);
        toast.success('Company updated successfully');
      } else {
        // Create new company - ensure status has a default value
        const createData = data as CreateCompanyRequest;
        await mockCompanyService.createCompany({
          name: createData.name,
          code: createData.code,
          status: createData.status || 'setup',
        });
        toast.success('Company created successfully');
      }
      
      // Reload companies list
      await loadCompanies();
      setIsFormOpen(false);
    } catch (error) {
      console.error('Error saving company:', error);
      throw error; // Re-throw to let the form handle the error
    }
  };

  const handleValidateCode = async (code: string, excludeId?: string): Promise<boolean> => {
    try {
      return await mockCompanyService.validateCompanyCode(code, excludeId);
    } catch (error) {
      console.error('Error validating company code:', error);
      return false;
    }
  };

  const handleSaveIntegration = async (updatedCompany: any) => {
    try {
      // TODO: Replace with actual API call to save product instances
      // await mockCompanyService.updateProductInstances(updatedCompany.id, updatedCompany.productInstances);
      
      toast.success('Integration configuration saved successfully');
      await loadCompanies();
    } catch (error) {
      console.error('Error saving integration configuration:', error);
      toast.error('Failed to save integration configuration');
    }
  };

  return (
    <div className="container mx-auto py-6">
      <CompanyList
        companies={companies}
        isLoading={isLoading}
        onCompanyClick={handleCompanyClick}
        onCompanyEdit={handleCompanyEdit}
        onCompanyCreate={handleCompanyCreate}
        onConfigureIntegrations={handleConfigureIntegrations}
        onRefresh={loadCompanies}
      />

      <CompanyFormDialog
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        company={editingCompany}
        onSubmit={handleFormSubmit}
        onValidateCode={handleValidateCode}
      />

      <IntegrationConfigDialog
        open={isIntegrationDialogOpen}
        onOpenChange={setIsIntegrationDialogOpen}
        company={selectedCompany}
        onSave={handleSaveIntegration}
      />
    </div>
  );
}
